import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTER_MY_NETWORK } from 'app/AppRouter';
import { getUserRights, UserRole } from 'domains/user';
import MyNetworkTable from 'pages/MyNetworkPage/Tables/MyNetworkTable';
import ClientsOrdersTable from 'pages/MyOrdersPage/ClientsOrders/Tables/ClientsOrdersTable';
import { Container, Flex, MarginBox, Tab, TabItems, Tabs, Text } from 'UI';
import { getCondArrayItem, getData, hasUserAnyRight } from 'utils';
import { useResetScroll } from 'utils/hooks/useResetScroll';

export const CLIENTS_TAB = 'clients';
export const NETWORK_ORDERS_TAB = 'network-orders';
const TAB_PARAM = 'tab';

type MyNetworkTab = typeof CLIENTS_TAB | typeof NETWORK_ORDERS_TAB;

const resolveActiveTab = (tabParam: string | null): MyNetworkTab => {
  if (tabParam === NETWORK_ORDERS_TAB) return NETWORK_ORDERS_TAB;
  return CLIENTS_TAB;
};

export const MyNetworkPage = (): JSX.Element => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const userRights = useSelector(getUserRights);
  const isR1 = hasUserAnyRight([UserRole.R1], getData(userRights));
  useResetScroll();

  const tabParam = new URLSearchParams(search).get(TAB_PARAM);
  const activeTab = resolveActiveTab(tabParam);

  const handleTabChange = (key: string) => {
    history.replace(`${ROUTER_MY_NETWORK}?${TAB_PARAM}=${key}`);
  };

  const tabItems: TabItems[] = [
    ...getCondArrayItem(
      isR1 && {
        key: CLIENTS_TAB,
        label: t('sidebar.r1.my_network_clients', 'My Network Clients'),
        component: <MyNetworkTable />,
      },
    ),
    ...getCondArrayItem(
      isR1 && {
        key: NETWORK_ORDERS_TAB,
        label: t('sidebar.r1.my_network_orders', 'My Network Orders'),
        component: <ClientsOrdersTable />,
      },
    ),
  ];

  return (
    <Container>
      <MarginBox mt={25}>
        <Flex direction={'column'}>
          <Text type={'h0'} data-cy={'my-profile-title'}>
            {t('profile.my_network', 'My Network')}
          </Text>
          <Tabs activeKey={activeTab} onChange={handleTabChange} tabBarGutter={15}>
            {tabItems?.map((item) => (
              <Tab tab={item.label} key={item.key}>
                {item.component}
              </Tab>
            ))}
          </Tabs>
        </Flex>
      </MarginBox>
    </Container>
  );
};
